import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import InputFileUpload from "../inputs/InputFileUpload";
import LanguageToggle from "../global/LanguageToggle";

const FormServiceSettings = ({ currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [headerTitleAr, setHeaderTitleAr] = useState("");
    const [headerTitleEn, setHeaderTitleEn] = useState("");
    const [headerColoredTitleAr, setHeaderColoredTitleAr] = useState("");
    const [headerColoredTitleEn, setHeaderColoredTitleEn] = useState("");
    const [headerDescriptionAr, setHeaderDescriptionAr] = useState("");
    const [headerDescriptionEn, setHeaderDescriptionEn] = useState("");
    const [homeTitleAr, setHomeTitleAr] = useState("");
    const [homeTitleEn, setHomeTitleEn] = useState("");
    const [homeSubtitleAr, setHomeSubtitleAr] = useState("");
    const [homeSubtitleEn, setHomeSubtitleEn] = useState("");
    const [homeDescriptionAr, setHomeDescriptionAr] = useState("");
    const [homeDescriptionEn, setHomeDescriptionEn] = useState("");
    const [image, setImage] = useState(null);

    useEffect(() => {
        if (currentData) {
            setHeaderTitleAr(currentData?.header_title?.ar || "");
            setHeaderTitleEn(currentData?.header_title?.en || "");
            setHeaderColoredTitleAr(currentData?.header_colored_title?.ar || "");
            setHeaderColoredTitleEn(currentData?.header_colored_title?.en || "");
            setHeaderDescriptionAr(currentData?.header_description?.ar || "");
            setHeaderDescriptionEn(currentData?.header_description?.en || "");
            setHomeTitleAr(currentData?.home_title?.ar || "");
            setHomeTitleEn(currentData?.home_title?.en || "");
            setHomeSubtitleAr(currentData?.home_subtitle?.ar || "");
            setHomeSubtitleEn(currentData?.home_subtitle?.en || "");
            setHomeDescriptionAr(currentData?.home_description?.ar || "");
            setHomeDescriptionEn(currentData?.home_description?.en || "");
            setImage(currentData?.image || null);
        }
    }, [currentData]);

    const handleImageChange = (e) => {
        const val = e.target.value;
        setImage(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            header_title: { ar: headerTitleAr || headerTitleEn, en: headerTitleEn || headerTitleAr },
            header_colored_title: { ar: headerColoredTitleAr || headerColoredTitleEn, en: headerColoredTitleEn || headerColoredTitleAr },
            header_description: { ar: headerDescriptionAr || headerDescriptionEn, en: headerDescriptionEn || headerDescriptionAr },
            home_title: { ar: homeTitleAr || homeTitleEn, en: homeTitleEn || homeTitleAr },
            home_subtitle: { ar: homeSubtitleAr || homeSubtitleEn, en: homeSubtitleEn || homeSubtitleAr },
            home_description: { ar: homeDescriptionAr || homeDescriptionEn, en: homeDescriptionEn || homeDescriptionAr },
            image: image,
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? "إعدادات صفحة الخدمات" : "Service Page Settings"}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <InputFileUpload
                            type="file"
                            name="image"
                            labelImage={isArabic ? "الصورة" : "Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {isArabic ? "بيانات الهيدر" : "Header Data"}
                        </Typography>
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="عنوان الهيدر (عربي)" placeholder="أدخل عنوان الهيدر بالعربي" value={headerTitleAr} onChange={(e) => setHeaderTitleAr(e.target.value)} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="Header Title (English)" placeholder="Enter header title in English" value={headerTitleEn} onChange={(e) => setHeaderTitleEn(e.target.value)} fullWidth />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="العنوان الملون (عربي)" placeholder="أدخل العنوان الملون بالعربي" value={headerColoredTitleAr} onChange={(e) => setHeaderColoredTitleAr(e.target.value)} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="Colored Title (English)" placeholder="Enter colored title in English" value={headerColoredTitleEn} onChange={(e) => setHeaderColoredTitleEn(e.target.value)} fullWidth />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput type="textarea" label="وصف الهيدر (عربي)" placeholder="أدخل وصف الهيدر بالعربي" value={headerDescriptionAr} onChange={(e) => setHeaderDescriptionAr(e.target.value)} multiline rows={3} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput type="textarea" label="Header Description (English)" placeholder="Enter header description in English" value={headerDescriptionEn} onChange={(e) => setHeaderDescriptionEn(e.target.value)} multiline rows={3} fullWidth />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {isArabic ? "بيانات القسم الرئيسي" : "Home Section Data"}
                        </Typography>
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="عنوان القسم الرئيسي (عربي)" placeholder="أدخل العنوان بالعربي" value={homeTitleAr} onChange={(e) => setHomeTitleAr(e.target.value)} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="Home Title (English)" placeholder="Enter home title in English" value={homeTitleEn} onChange={(e) => setHomeTitleEn(e.target.value)} fullWidth />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="العنوان الفرعي (عربي)" placeholder="أدخل العنوان الفرعي بالعربي" value={homeSubtitleAr} onChange={(e) => setHomeSubtitleAr(e.target.value)} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="Home Subtitle (English)" placeholder="Enter home subtitle in English" value={homeSubtitleEn} onChange={(e) => setHomeSubtitleEn(e.target.value)} fullWidth />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput type="textarea" label="وصف القسم الرئيسي (عربي)" placeholder="أدخل الوصف بالعربي" value={homeDescriptionAr} onChange={(e) => setHomeDescriptionAr(e.target.value)} multiline rows={3} fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput type="textarea" label="Home Description (English)" placeholder="Enter home description in English" value={homeDescriptionEn} onChange={(e) => setHomeDescriptionEn(e.target.value)} multiline rows={3} fullWidth />
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" disabled={isLoading} sx={{ minWidth: 120 }}>
                        {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormServiceSettings;
